'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function TimeDurationCalculator() {
  const t = useTranslations("tools.timeDurationCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Simple time calculator state
  const [simpleInputs, setSimpleInputs] = useState({
    startHour: 8,
    startMin: 30,
    startSec: 0,
    startPeriod: 'AM',
    endHour: 5,
    endMin: 30,
    endSec: 0,
    endPeriod: 'PM'
  });
  const [simpleResults, setSimpleResults] = useState<any>(null);

  // Date-time calculator state
  const [dateInputs, setDateInputs] = useState({
    startDate: '',
    startHour: 8,
    startMin: 30,
    startSec: 0,
    startPeriod: 'AM',
    endDate: '',
    endHour: 5,
    endMin: 30,
    endSec: 0,
    endPeriod: 'PM'
  });
  const [dateResults, setDateResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);

    // Set default dates to today
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;
    setDateInputs(prev => ({ ...prev, startDate: todayStr, endDate: todayStr }));

    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1e40af',
    secondary: '#7c3aed',
    accent: '#f59e0b',
    success: '#15803d',
    textPrimary: '#1e40af',
    textSecondary: '#64748b',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorSection: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem' },
      { padding: '3rem 1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '900px', margin: '0 auto' },
      {},
      {},
      {}
    ),
    calculatorCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    h2: getResponsiveStyle(
      { color: colors.primary, marginBottom: '1.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    h3: getResponsiveStyle(
      { color: colors.textPrimary, marginBottom: '1rem', fontWeight: 600 },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' }
    ),
    timeInputGrid: getResponsiveStyle(
      { display: 'grid', gap: '1rem', alignItems: 'center', marginBottom: '1rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'auto 1fr 1fr 1fr auto auto' },
      { gridTemplateColumns: 'auto 1fr 1fr 1fr auto auto' }
    ),
    timeLabel: {
      fontWeight: 600,
      color: colors.textPrimary,
      whiteSpace: 'nowrap' as const
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.25rem'
    },
    inputLabel: {
      fontSize: '0.875rem',
      color: colors.textSecondary,
      textAlign: 'center' as const
    },
    input: {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      textAlign: 'center' as const,
      transition: 'all 0.3s ease',
      width: '100%'
    },
    select: {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    nowBtn: getResponsiveStyle(
      {
        background: colors.secondary,
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        whiteSpace: 'nowrap' as const,
        fontWeight: 500
      },
      { padding: '0.5rem 0.75rem', fontSize: '0.875rem' },
      { padding: '0.5rem 1rem', fontSize: '0.875rem' },
      { padding: '0.5rem 1rem', fontSize: '0.875rem' }
    ),
    swapBtn: {
      background: colors.accent,
      color: 'white',
      border: 'none',
      padding: '0.5rem',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontSize: '1.25rem',
      transition: 'all 0.3s ease',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '1rem auto'
    },
    dateTimeRow: getResponsiveStyle(
      { display: 'grid', gap: '1rem', alignItems: 'start', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '150px 1fr' },
      { gridTemplateColumns: '150px 1fr' }
    ),
    dateInput: {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      width: '100%',
      transition: 'all 0.3s ease'
    },
    timeInputsInline: getResponsiveStyle(
      { display: 'grid', gap: '0.5rem', alignItems: 'end', marginTop: '0.5rem' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: 'repeat(4, 1fr) auto' },
      { gridTemplateColumns: 'repeat(4, 1fr) auto' }
    ),
    buttonGroup: getResponsiveStyle(
      { display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' },
      { flexDirection: 'column' as const },
      { flexDirection: 'row' as const },
      { flexDirection: 'row' as const }
    ),
    calculateBtn: getResponsiveStyle(
      {
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 6px rgba(37, 99, 235, 0.3)'
      },
      { padding: '1rem 2rem', fontSize: '1rem', width: '100%' },
      { padding: '1rem 3rem', fontSize: '1.125rem', width: 'auto' },
      { padding: '1rem 3rem', fontSize: '1.125rem', width: 'auto' }
    ),
    clearBtn: getResponsiveStyle(
      {
        background: '#64748b',
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      },
      { padding: '1rem 2rem', fontSize: '1rem', width: '100%' },
      { padding: '1rem 3rem', fontSize: '1.125rem', width: 'auto' },
      { padding: '1rem 3rem', fontSize: '1.125rem', width: 'auto' }
    ),
    results: {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '1rem',
      padding: '2rem',
      marginTop: '2rem',
      border: '2px solid #86efac'
    },
    resultsTitle: getResponsiveStyle(
      { color: colors.success, marginBottom: '1.5rem', textAlign: 'center' as const, fontWeight: 600 },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    resultGrid: getResponsiveStyle(
      { display: 'grid', gap: '1rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
    ),
    resultItem: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
    },
    resultLabel: {
      display: 'block',
      fontSize: '0.875rem',
      color: colors.textSecondary,
      marginBottom: '0.5rem',
      fontWeight: 500
    },
    resultValue: getResponsiveStyle(
      { display: 'block', fontWeight: 700, color: colors.success },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    resultUnit: {
      fontSize: '1rem',
      color: colors.textSecondary,
      marginLeft: '0.25rem'
    },
    errorMessage: {
      background: '#fef2f2',
      border: '2px solid #fca5a5',
      color: '#dc2626',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      textAlign: 'center' as const
    },
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, marginLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem'
    },
    strong: {
      fontWeight: 600,
      color: colors.textPrimary
    }
  };

  const convertTo24Hour = (hour: number, period: string): number => {
    if (period === 'AM') {
      return hour === 12 ? 0 : hour;
    } else {
      return hour === 12 ? 12 : hour + 12;
    }
  };

  const setCurrentTime = (type: 'simple' | 'date', position: 'start' | 'end') => {
    const now = new Date();
    let hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();
    const period = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    if (hours === 0) hours = 12;

    if (type === 'simple') {
      if (position === 'start') {
        setSimpleInputs(prev => ({
          ...prev,
          startHour: hours,
          startMin: minutes,
          startSec: seconds,
          startPeriod: period
        }));
      } else {
        setSimpleInputs(prev => ({
          ...prev,
          endHour: hours,
          endMin: minutes,
          endSec: seconds,
          endPeriod: period
        }));
      }
    } else {
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;

      if (position === 'start') {
        setDateInputs(prev => ({
          ...prev,
          startDate: todayStr,
          startHour: hours,
          startMin: minutes,
          startSec: seconds,
          startPeriod: period
        }));
      } else {
        setDateInputs(prev => ({
          ...prev,
          endDate: todayStr,
          endHour: hours,
          endMin: minutes,
          endSec: seconds,
          endPeriod: period
        }));
      }
    }
  };

  const swapSimpleTimes = () => {
    setSimpleInputs(prev => ({
      startHour: prev.endHour,
      startMin: prev.endMin,
      startSec: prev.endSec,
      startPeriod: prev.endPeriod,
      endHour: prev.startHour,
      endMin: prev.startMin,
      endSec: prev.startSec,
      endPeriod: prev.startPeriod
    }));
  };

  const handleSimpleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const start24 = convertTo24Hour(simpleInputs.startHour, simpleInputs.startPeriod);
    const end24 = convertTo24Hour(simpleInputs.endHour, simpleInputs.endPeriod);

    let startTotalSeconds = start24 * 3600 + simpleInputs.startMin * 60 + simpleInputs.startSec;
    let endTotalSeconds = end24 * 3600 + simpleInputs.endMin * 60 + simpleInputs.endSec;

    if (endTotalSeconds < startTotalSeconds) {
      endTotalSeconds += 24 * 3600;
    }

    const diffSeconds = endTotalSeconds - startTotalSeconds;

    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    const seconds = diffSeconds % 60;
    const totalHours = (diffSeconds / 3600).toFixed(2);

    setSimpleResults({ hours, minutes, seconds, totalHours, totalSeconds: diffSeconds });
  };

  const handleDateCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const start24 = convertTo24Hour(dateInputs.startHour, dateInputs.startPeriod);
    const end24 = convertTo24Hour(dateInputs.endHour, dateInputs.endPeriod);

    const startDateTime = new Date(dateInputs.startDate);
    startDateTime.setHours(start24, dateInputs.startMin, dateInputs.startSec, 0);

    const endDateTime = new Date(dateInputs.endDate);
    endDateTime.setHours(end24, dateInputs.endMin, dateInputs.endSec, 0);

    const diffMs = endDateTime.getTime() - startDateTime.getTime();

    if (diffMs < 0) {
      setDateResults({ error: true });
      return;
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    const days = totalDays;
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;
    const seconds = totalSeconds % 60;

    setDateResults({
      days,
      hours,
      minutes,
      seconds,
      totalSeconds,
      totalMinutes,
      totalHours: (totalSeconds / 3600).toFixed(2),
      totalDays: (totalSeconds / 86400).toFixed(2)
    });
  };

  const clearSimple = () => {
    setSimpleInputs({
      startHour: 8,
      startMin: 30,
      startSec: 0,
      startPeriod: 'AM',
      endHour: 5,
      endMin: 30,
      endSec: 0,
      endPeriod: 'PM'
    });
    setSimpleResults(null);
  };

  const clearDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;

    setDateInputs({
      startDate: todayStr,
      startHour: 8,
      startMin: 30,
      startSec: 0,
      startPeriod: 'AM',
      endDate: todayStr,
      endHour: 5,
      endMin: 30,
      endSec: 0,
      endPeriod: 'PM'
    });
    setDateResults(null);
  };

  const renderResults = (results: any) => {
    if (!results) return null;

    if (results.error) {
      return (
        <div style={styles.errorMessage}>
          {t("errorEndBeforeStart")}
        </div>
      );
    }

    return (
      <div style={styles.results}>
        <h3 style={styles.resultsTitle}>{t("resultsTitle")}</h3>
        <div style={styles.resultGrid}>
          {results.days !== undefined && (
            <div style={styles.resultItem}>
              <span style={styles.resultLabel}>{t("days")}</span>
              <span style={styles.resultValue}>
                {results.days}
                <span style={styles.resultUnit}>{t("daysUnit")}</span>
              </span>
            </div>
          )}
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t("hours")}</span>
            <span style={styles.resultValue}>
              {results.hours}
              <span style={styles.resultUnit}>{t("hoursUnit")}</span>
            </span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t("minutes")}</span>
            <span style={styles.resultValue}>
              {results.minutes}
              <span style={styles.resultUnit}>{t("minutesUnit")}</span>
            </span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t("seconds")}</span>
            <span style={styles.resultValue}>
              {results.seconds}
              <span style={styles.resultUnit}>{t("secondsUnit")}</span>
            </span>
          </div>
          {results.totalDays !== undefined && (
            <div style={styles.resultItem}>
              <span style={styles.resultLabel}>{t("totalDays")}</span>
              <span style={styles.resultValue}>
                {results.totalDays}
                <span style={styles.resultUnit}>{t("daysUnit")}</span>
              </span>
            </div>
          )}
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t("totalHours")}</span>
            <span style={styles.resultValue}>
              {results.totalHours}
              <span style={styles.resultUnit}>{t("hoursUnit")}</span>
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      <section style={styles.calculatorSection}>
        <div style={styles.toolContainer}>
          <div style={styles.calculatorCard}>
            <h2 style={styles.h2}>
              <span>⏰</span>
              {t("simpleCalculatorTitle")}
            </h2>
            <form onSubmit={handleSimpleCalculate}>
              <div style={styles.timeInputGrid}>
                <div style={styles.timeLabel}>{t("startTime")}</div>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>{t("hour")}</label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={simpleInputs.startHour}
                    onChange={(e) => setSimpleInputs({...simpleInputs, startHour: parseInt(e.target.value) || 1})}
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>{t("minute")}</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={simpleInputs.startMin}
                    onChange={(e) => setSimpleInputs({...simpleInputs, startMin: parseInt(e.target.value) || 0})}
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>{t("second")}</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={simpleInputs.startSec}
                    onChange={(e) => setSimpleInputs({...simpleInputs, startSec: parseInt(e.target.value) || 0})}
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>{t("period")}</label>
                  <select
                    value={simpleInputs.startPeriod}
                    onChange={(e) => setSimpleInputs({...simpleInputs, startPeriod: e.target.value})}
                    style={styles.select}
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
                <button type="button" style={styles.nowBtn} onClick={() => setCurrentTime('simple', 'start')}>
                  {t("now")}
                </button>
              </div>

              <div style={{ textAlign: 'center' }}>
                <button type="button" style={styles.swapBtn} onClick={swapSimpleTimes}>
                  ⇅
                </button>
              </div>

              <div style={styles.timeInputGrid}>
                <div style={styles.timeLabel}>{t("endTime")}</div>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>{t("hour")}</label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={simpleInputs.endHour}
                    onChange={(e) => setSimpleInputs({...simpleInputs, endHour: parseInt(e.target.value) || 1})}
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>{t("minute")}</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={simpleInputs.endMin}
                    onChange={(e) => setSimpleInputs({...simpleInputs, endMin: parseInt(e.target.value) || 0})}
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>{t("second")}</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={simpleInputs.endSec}
                    onChange={(e) => setSimpleInputs({...simpleInputs, endSec: parseInt(e.target.value) || 0})}
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>{t("period")}</label>
                  <select
                    value={simpleInputs.endPeriod}
                    onChange={(e) => setSimpleInputs({...simpleInputs, endPeriod: e.target.value})}
                    style={styles.select}
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
                <button type="button" style={styles.nowBtn} onClick={() => setCurrentTime('simple', 'end')}>
                  {t("now")}
                </button>
              </div>

              <div style={styles.buttonGroup}>
                <button type="submit" style={styles.calculateBtn}>
                  {t("calculateButton")}
                </button>
                <button type="button" style={styles.clearBtn} onClick={clearSimple}>
                  {t("clearButton")}
                </button>
              </div>
            </form>

            {renderResults(simpleResults)}
          </div>

          <div style={styles.calculatorCard}>
            <h2 style={styles.h2}>
              <span>📅</span>
              {t("dateCalculatorTitle")}
            </h2>
            <form onSubmit={handleDateCalculate}>
              <div style={styles.dateTimeRow}>
                <div style={styles.timeLabel}>{t("startDateTime")}</div>
                <div>
                  <input
                    type="date"
                    value={dateInputs.startDate}
                    onChange={(e) => setDateInputs({...dateInputs, startDate: e.target.value})}
                    style={styles.dateInput}
                    required
                  />
                  <div style={styles.timeInputsInline}>
                    <div style={styles.inputGroup}>
                      <label style={styles.inputLabel}>{t("hour")}</label>
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={dateInputs.startHour}
                        onChange={(e) => setDateInputs({...dateInputs, startHour: parseInt(e.target.value) || 1})}
                        style={styles.input}
                        required
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.inputLabel}>{t("minute")}</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={dateInputs.startMin}
                        onChange={(e) => setDateInputs({...dateInputs, startMin: parseInt(e.target.value) || 0})}
                        style={styles.input}
                        required
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.inputLabel}>{t("second")}</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={dateInputs.startSec}
                        onChange={(e) => setDateInputs({...dateInputs, startSec: parseInt(e.target.value) || 0})}
                        style={styles.input}
                        required
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.inputLabel}>{t("period")}</label>
                      <select
                        value={dateInputs.startPeriod}
                        onChange={(e) => setDateInputs({...dateInputs, startPeriod: e.target.value})}
                        style={styles.select}
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                    <button type="button" style={styles.nowBtn} onClick={() => setCurrentTime('date', 'start')}>
                      {t("now")}
                    </button>
                  </div>
                </div>
              </div>

              <div style={styles.dateTimeRow}>
                <div style={styles.timeLabel}>{t("endDateTime")}</div>
                <div>
                  <input
                    type="date"
                    value={dateInputs.endDate}
                    onChange={(e) => setDateInputs({...dateInputs, endDate: e.target.value})}
                    style={styles.dateInput}
                    required
                  />
                  <div style={styles.timeInputsInline}>
                    <div style={styles.inputGroup}>
                      <label style={styles.inputLabel}>{t("hour")}</label>
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={dateInputs.endHour}
                        onChange={(e) => setDateInputs({...dateInputs, endHour: parseInt(e.target.value) || 1})}
                        style={styles.input}
                        required
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.inputLabel}>{t("minute")}</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={dateInputs.endMin}
                        onChange={(e) => setDateInputs({...dateInputs, endMin: parseInt(e.target.value) || 0})}
                        style={styles.input}
                        required
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.inputLabel}>{t("second")}</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={dateInputs.endSec}
                        onChange={(e) => setDateInputs({...dateInputs, endSec: parseInt(e.target.value) || 0})}
                        style={styles.input}
                        required
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.inputLabel}>{t("period")}</label>
                      <select
                        value={dateInputs.endPeriod}
                        onChange={(e) => setDateInputs({...dateInputs, endPeriod: e.target.value})}
                        style={styles.select}
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                    <button type="button" style={styles.nowBtn} onClick={() => setCurrentTime('date', 'end')}>
                      {t("now")}
                    </button>
                  </div>
                </div>
              </div>

              <div style={styles.buttonGroup}>
                <button type="submit" style={styles.calculateBtn}>
                  {t("calculateButton")}
                </button>
                <button type="button" style={styles.clearBtn} onClick={clearDate}>
                  {t("clearButton")}
                </button>
              </div>
            </form>

            {renderResults(dateResults)}
          </div>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <h3 style={styles.h3}>{t("simpleTimeCalculation")}</h3>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t("step1Simple")}</li>
          <li style={styles.listItem}>{t("step2Simple")}</li>
          <li style={styles.listItem}>{t("step3Simple")}</li>
          <li style={styles.listItem}>{t("step4Simple")}</li>
        </ol>

        <h3 style={styles.h3}>{t("dateTimeCalculation")}</h3>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t("step1Date")}</li>
          <li style={styles.listItem}>{t("step2Date")}</li>
          <li style={styles.listItem}>{t("step3Date")}</li>
          <li style={styles.listItem}>{t("step4Date")}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <h3 style={styles.h3}>{t("whatCalculatorDoes")}</h3>
        <p style={styles.paragraph}>{t("calculatorFunction1")}</p>
        <p style={styles.paragraph}>{t("calculatorFunction2")}</p>
        <p style={styles.paragraph}>{t("calculatorFunction3")}</p>

        <h3 style={styles.h3}>{t("whyUseful")}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            <span style={styles.strong}>{t("workPayroll")}</span> {t("workPayrollDesc")}
          </li>
          <li style={styles.listItem}>
            <span style={styles.strong}>{t("projectsProductivity")}</span> {t("projectsProductivityDesc")}
          </li>
          <li style={styles.listItem}>
            <span style={styles.strong}>{t("schedulingEvents")}</span> {t("schedulingEventsDesc")}
          </li>
          <li style={styles.listItem}>
            <span style={styles.strong}>{t("generalTimeMath")}</span> {t("generalTimeMathDesc")}
          </li>
        </ul>

        <h3 style={styles.h3}>{t("latestGuidance")}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            <span style={styles.strong}>{t("accuracyErrorReduction")}</span> {t("accuracyErrorReductionDesc")}
          </li>
          <li style={styles.listItem}>
            <span style={styles.strong}>{t("complianceRecordKeeping")}</span> {t("complianceRecordKeepingDesc")}
          </li>
          <li style={styles.listItem}>
            <span style={styles.strong}>{t("decimalHours")}</span> {t("decimalHoursDesc")}
          </li>
          <li style={styles.listItem}>
            <span style={styles.strong}>{t("crossDayAwareness")}</span> {t("crossDayAwarenessDesc")}
          </li>
          <li style={styles.listItem}>
            <span style={styles.strong}>{t("timeManagementIntegration")}</span> {t("timeManagementIntegrationDesc")}
          </li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("bestPracticesTitle")}</h2>
        <h3 style={styles.h3}>{t("inputClarity")}</h3>
        <p style={styles.paragraph}>{t("inputClarityDesc")}</p>

        <h3 style={styles.h3}>{t("handlingDates")}</h3>
        <p style={styles.paragraph}>{t("handlingDatesDesc")}</p>

        <h3 style={styles.h3}>{t("breaksOvertime")}</h3>
        <p style={styles.paragraph}>{t("breaksOvertimeDesc")}</p>

        <h3 style={styles.h3}>{t("outputFormats")}</h3>
        <p style={styles.paragraph}>{t("outputFormatsDesc")}</p>

        <h3 style={styles.h3}>{t("edgeCases")}</h3>
        <p style={styles.paragraph}>{t("edgeCasesDesc")}</p>

        <h3 style={styles.h3}>{t("userExperience")}</h3>
        <p style={styles.paragraph}>{t("userExperienceDesc")}</p>

        <h3 style={styles.h3}>{t("roundingPrecision")}</h3>
        <p style={styles.paragraph}>{t("roundingPrecisionDesc")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <h3 style={styles.h3}>{t("faq1Question")}</h3>
        <p style={styles.paragraph}>{t("faq1Answer")}</p>

        <h3 style={styles.h3}>{t("faq2Question")}</h3>
        <p style={styles.paragraph}>{t("faq2Answer")}</p>

        <h3 style={styles.h3}>{t("faq3Question")}</h3>
        <p style={styles.paragraph}>{t("faq3Answer")}</p>

        <h3 style={styles.h3}>{t("faq4Question")}</h3>
        <p style={styles.paragraph}>{t("faq4Answer")}</p>

        <h3 style={styles.h3}>{t("faq5Question")}</h3>
        <p style={styles.paragraph}>{t("faq5Answer")}</p>

        <h3 style={styles.h3}>{t("faq6Question")}</h3>
        <p style={styles.paragraph}>{t("faq6Answer")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("limitationsTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("limitation1")}</li>
          <li style={styles.listItem}>{t("limitation2")}</li>
          <li style={styles.listItem}>{t("limitation3")}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <p style={styles.paragraph}>{t("referencesIntro")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("reference1")}</li>
          <li style={styles.listItem}>{t("reference2")}</li>
          <li style={styles.listItem}>{t("reference3")}</li>
          <li style={styles.listItem}>{t("reference4")}</li>
        </ul>
      </section>
    </div>
  );
}